import { useState, useRef, useEffect } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export interface AutocompleteOption {
  id: string;
  label: string;
  value: string;
}

interface AutocompleteProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (option: AutocompleteOption) => void;
  suggestions: AutocompleteOption[];
  isLoading?: boolean;
  error?: string;
  debounceMs?: number;
  forceUppercase?: boolean;
}

export const Autocomplete = ({
  label,
  placeholder,
  value,
  onChange,
  onSelect,
  suggestions,
  isLoading,
  error,
  forceUppercase = false,
}: AutocompleteProps) => {
  const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null);
  const [query, setQuery] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleChange = (newQuery: string) => {
    const processedQuery = forceUppercase ? newQuery.toUpperCase() : newQuery;
    setQuery(processedQuery);
    onChange(processedQuery);
    setSelectedOption(null);
  };

  const handleSelect = (option: AutocompleteOption | null) => {
    if (option) {
      setSelectedOption(option);
      setQuery(option.label);
      onChange(option.label);
      if (onSelect) {
        onSelect(option);
      }
    }
  };

  const filteredSuggestions = suggestions;

  return (
    <div className="mb-4">
      {label && (
        <label className="mb-2 block text-xs font-bold uppercase text-gray-700">{label}</label>
      )}
      <Combobox value={selectedOption} onChange={handleSelect}>
        <div className="relative">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <Combobox.Input
              ref={inputRef}
              className={clsx(
                'w-full rounded-input border border-gray-400 py-2 pl-10 pr-10 text-m transition-colors focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold',
                {
                  'border-system-red focus:border-system-red focus:ring-system-red': error,
                }
              )}
              placeholder={placeholder}
              displayValue={() => query}
              onChange={(event) => handleChange(event.target.value)}
            />
            {isLoading && (
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gold border-t-transparent" />
              </div>
            )}
          </div>

          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white py-1 shadow-lg focus:outline-none">
              {filteredSuggestions.length === 0 && query !== '' && !isLoading ? (
                <div className="relative cursor-default select-none px-4 py-2 text-s text-gray-600">
                  {query.length < 3
                    ? 'Type at least 3 characters to search'
                    : 'No addresses found'}
                </div>
              ) : (
                filteredSuggestions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        'relative cursor-pointer select-none py-2 pl-10 pr-4 text-s',
                        active ? 'bg-gold-light text-gold' : 'text-gray-900'
                      )
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={clsx('block truncate', selected ? 'font-bold' : 'font-normal')}
                        >
                          {option.label}
                        </span>
                        {selected && (
                          <span
                            className={clsx(
                              'absolute inset-y-0 left-0 flex items-center pl-3',
                              active ? 'text-gold' : 'text-gold'
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {error && <p className="mt-1 text-xs text-system-red">{error}</p>}
    </div>
  );
};
